import {
  type AnswerOption,
  formatSelectionDisplay,
  formatSelectionFromLetters,
  selectionsAreEquivalent,
} from './selection-format';

export type VerificationResultFilter =
  | 'all'
  | 'correct'
  | 'attention'
  | 'proofreading_failed'
  | 'customer_approval_requested';

export const ANOMALY_SEVERITIES = [
  'critique',
  'majeur',
  'mineur',
  'info',
] as const;

export type AnomalySeverity = (typeof ANOMALY_SEVERITIES)[number];

/** Ordre de tri : critique d'abord, non classé en dernier. */
export function anomalySeverityRank(severity?: AnomalySeverity): number {
  const index = ANOMALY_SEVERITIES.indexOf(severity as AnomalySeverity);
  return index === -1 ? ANOMALY_SEVERITIES.length : index;
}

type Proofreading = {
  errors?: Array<{
    rule_name: string;
    description: string;
    severity?: AnomalySeverity;
  }>;
  status: 'Valide' | 'Non valide';
  comment?: string;
};

export type SecondOpinion = {
  verdict: 'Confirme' | 'Nuance' | 'Conteste';
  argumentation: string;
  pointsDeDesaccord?: string;
  citations: string[];
  cost: number;
  model: string;
  createdAt: string;
};

type VerificationResultProps = {
  processId: string;
  questionAnswerPairId: string;
  answer: string;
  citations?: string[];
  cost: string;
  proofreading?: Proofreading;
  input: {
    question: string;
    questionType: 'QCM' | 'QCU';
    questionTarget: 'Particulier' | 'Professionnel';
    questionSkills: string;
    propositions: string;
    selection: string;
    feedback: string;
  };
  selection: string;
  editedSelection?: string | null;
  secondOpinion?: SecondOpinion | null;
  createdAt: string;
  customerApprovalRequested: boolean;
  internalComment?: string;
  sheetFeedback?: string;
};

export class VerificationResult {
  processId: string;
  questionAnswerPairId: string;
  answer: string;
  citations?: string[];
  cost: string;
  proofreading?: Proofreading;
  input: {
    question: string;
    questionType: 'QCM' | 'QCU';
    questionTarget: 'Particulier' | 'Professionnel';
    questionSkills: string;
    propositions: string;
    selection: string;
    feedback: string;
  };
  selection: string;
  editedSelection?: string | null;
  secondOpinion?: SecondOpinion | null;
  createdAt: string;
  customerApprovalRequested: boolean;
  internalComment?: string;
  sheetFeedback?: string;

  constructor(props: VerificationResultProps) {
    this.processId = props.processId;
    this.questionAnswerPairId = props.questionAnswerPairId;
    this.answer = props.answer;
    this.citations = props.citations;
    this.cost = props.cost;
    this.proofreading = props.proofreading;
    this.input = props.input;
    this.selection = props.selection;
    this.editedSelection = props.editedSelection ?? null;
    this.secondOpinion = props.secondOpinion ?? null;
    this.createdAt = props.createdAt;
    this.customerApprovalRequested = props.customerApprovalRequested;
    this.internalComment = props.internalComment;
    this.sheetFeedback = props.sheetFeedback;
  }

  getVerifiedSelection(): string {
    return formatSelectionDisplay(this.selection);
  }

  getInputSelection(): string {
    return formatSelectionDisplay(this.input.selection);
  }

  getUserSelectionDisplay(localOverride: string | null): string {
    return formatSelectionDisplay(localOverride ?? '');
  }

  hasUserSelectionOverride(localOverride: string | null): boolean {
    if (!localOverride?.trim()) {
      return false;
    }

    return !selectionsAreEquivalent(localOverride, this.selection);
  }

  getAnswerOptions(): AnswerOption[] {
    return this.getPossibleAnswers()
      .map((option) => {
        const match = option.match(/^([A-E])\.\s*(.*)$/);
        return {
          letter: match?.[1] ?? '',
          label: match?.[2] ?? option,
        };
      })
      .filter((option) => option.letter.length > 0);
  }

  formatSelectionFromLetters(letters: string[]): string {
    return formatSelectionFromLetters(letters);
  }

  isCorrect(): boolean {
    return selectionsAreEquivalent(this.selection, this.input.selection);
  }

  getPossibleAnswers(): string[] {
    return this.input.propositions
      .split('\n')
      .map((entry) => entry.replace(/^\s*-\s*/, '').trim())
      .filter((entry) => entry.length > 0);
  }

  getPossibleAnswersWithoutLetterPrefix(): string[] {
    return this.input.propositions
      .split('\n')
      .map((entry) => entry.replace(/^\s*-\s*[A-E]\.\s*/, '').trim())
      .filter((entry) => entry.length > 0);
  }

  hasProofreadingErrors(): boolean {
    return !!(
      this.proofreading &&
      Array.isArray(this.proofreading.errors) &&
      this.proofreading.errors.length > 0
    );
  }

  getProofreadingErrors(): Required<Proofreading>['errors'] {
    return this.proofreading?.errors ?? [];
  }

  /**
   * Sévérité la plus élevée parmi les erreurs de relecture, ou undefined si
   * aucune erreur n'est classée (anciennes données).
   */
  getMaxProofreadingSeverity(): AnomalySeverity | undefined {
    return this.getProofreadingErrors()
      .map((error) => error.severity)
      .filter((severity): severity is AnomalySeverity => severity !== undefined)
      .sort((a, b) => anomalySeverityRank(a) - anomalySeverityRank(b))[0];
  }
}
